/**
 * Rachat manuel d'un étudiant (2026-10-03).
 *
 * Le jury voit toutes les notes relevables (rattrapage composé ; session
 * ordinaire seulement pour une ECUE sans rattrapage, signalée ; devoirs D1/D2
 * en cas de force majeure, 03/10/2026), part de la
 * proposition intelligente ou de zéro, simule : la moyenne affichée est celle du
 * calcul officiel (transaction annulée). L'application n'est possible que si la
 * simulation des notes saisies valide le semestre ; elle est tracée et annulable
 * depuis l'historique, comme un lot.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  useManuelDossierMutation, useManuelSimulerMutation, useManuelAppliquerMutation,
  ManuelDossier, ManuelSimulation, ManuelNote,
} from '../../services/rachatApi';

interface Props {
  etudiantId: number;
  semestre: 1 | 2;
  anneeAcademique: string;
  plafond: number;
  autoriserSo: boolean;
  /** Inclure les SO à 0 (réputées non-participation) */
  autoriserSoZero?: boolean;
  propagerAuxDevoirs: boolean;
  onClose: () => void;
  onApplique?: () => void;
}

const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toFixed(2));

const RachatManuelModal: React.FC<Props> = ({
  etudiantId, semestre, anneeAcademique, plafond, autoriserSo, autoriserSoZero = false, propagerAuxDevoirs, onClose, onApplique,
}) => {
  const [charger, { isLoading: chargement }] = useManuelDossierMutation();
  const [simuler, { isLoading: simulation }] = useManuelSimulerMutation();
  const [appliquer, { isLoading: application }] = useManuelAppliquerMutation();
  const [dossier, setDossier] = useState<ManuelDossier | null>(null);
  // Saisies par « matiere_id:source » (source = SO, SR, D1 ou D2)
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [resultat, setResultat] = useState<ManuelSimulation | null>(null);
  const [resultatPour, setResultatPour] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [confirme, setConfirme] = useState(false);
  const [fait, setFait] = useState<{ moy_avant: number | null; moy_apres: number | null; batch_id: string } | null>(null);

  const contexte = {
    etudiant_id: etudiantId, semestre, annee_academique: anneeAcademique,
    plafond_augmentation: plafond, autoriser_so: autoriserSo, autoriser_so_zero: autoriserSoZero,
    propager_aux_devoirs: propagerAuxDevoirs,
  };

  useEffect(() => {
    charger(contexte).unwrap()
      .then((d) => {
        setDossier(d);
        remplir(d, d.proposition_auto?.notes || []);
      })
      .catch((e: any) => setErreur(e?.data?.error || 'Dossier indisponible.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiantId, semestre, anneeAcademique, plafond, autoriserSo, autoriserSoZero]);

  const remplir = (d: ManuelDossier, notes: ManuelNote[]) => {
    const parCle = new Map(notes.map((n) => [`${n.matiere_id}:${n.source}`, n.note]));
    const s: Record<string, string> = {};
    const poser = (cle: string) => { s[cle] = parCle.has(cle) ? String(parCle.get(cle)) : ''; };
    d.ues.forEach((u) => u.ecues.forEach((e) => {
      if (e.source_relevable) poser(`${e.matiere_id}:${e.source_relevable}`);
      if (e.devoir_editable) { poser(`${e.matiere_id}:D1`); poser(`${e.matiere_id}:D2`); }
    }));
    setSaisies(s);
    setResultat(null);
    setConfirme(false);
  };

  const ecues = useMemo(() => (dossier ? dossier.ues.flatMap((u) => u.ecues) : []), [dossier]);

  // Notes réellement relevées (au-dessus de la note retenue actuelle) + contrôles de saisie
  const { notes, problemes } = useMemo(() => {
    const out: ManuelNote[] = [];
    const pb: string[] = [];
    const lire = (e: typeof ecues[number], source: ManuelNote['source'], actuel: number, maxi: number | null) => {
      const brut = (saisies[`${e.matiere_id}:${source}`] || '').trim().replace(',', '.');
      if (!brut) return;
      const v = Number(brut);
      if (Number.isNaN(v)) { pb.push(`${e.matiere_nom} ${source} : valeur invalide`); return; }
      if (v > (maxi ?? 20)) { pb.push(`${e.matiere_nom} ${source} : au-dessus du plafond (${fmt(maxi)})`); return; }
      if (v <= actuel) return; // pas de hausse : ignorée
      out.push({ matiere_id: e.matiere_id, source, note: Math.round(v * 100) / 100 });
    };
    ecues.forEach((e) => {
      if (e.source_relevable) lire(e, e.source_relevable, Math.max(e.so ?? 0, e.sr ?? 0), e.note_max);
      if (e.devoir_editable) {
        lire(e, 'D1', e.absent1 ? 0 : (e.d1 ?? 0), e.d1_max);
        lire(e, 'D2', e.absent2 ? 0 : (e.d2 ?? 0), e.d2_max);
      }
    });
    return { notes: out, problemes: pb };
  }, [ecues, saisies]);
  const signature = JSON.stringify(notes);
  const simulationAJour = resultat !== null && resultatPour === signature;

  const lancerSimulation = async () => {
    setErreur(null);
    try {
      const r = await simuler({ ...contexte, notes }).unwrap();
      setResultat(r);
      setResultatPour(signature);
    } catch (e: any) {
      setErreur(e?.data?.error || 'Simulation impossible.');
    }
  };

  const lancerApplication = async () => {
    setErreur(null);
    try {
      const r = await appliquer({ ...contexte, notes, commentaire }).unwrap();
      setFait(r);
      if (onApplique) onApplique();
    } catch (e: any) {
      setErreur(e?.data?.error || "Application refusée : aucune note n'a été modifiée.");
    }
  };

  const ueApres = (ueId: number) => resultat?.ues.find((u) => u.ue_id === ueId);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Rachat manuel</h2>
            {dossier && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {dossier.nom} · {dossier.matricule} · {dossier.classe} · S{semestre} {dossier.annee_academique}
                {' '}· moyenne actuelle <b className="text-gray-800 dark:text-gray-200">{fmt(dossier.moy_sem)}</b>
                {' '}· plafond +{plafond} sur la note retenue
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {chargement && <div className="py-10 text-center"><Loader className="animate-spin mx-auto" /></div>}
          {erreur && (
            <div className="flex items-start gap-2 p-3 rounded border border-red-200 bg-red-50 text-xs text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {erreur}
            </div>
          )}
          {fait && (
            <div className="flex items-start gap-2 p-3 rounded border border-green-200 bg-green-50 text-sm text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                Rachat appliqué : moyenne {fmt(fait.moy_avant)} → <b>{fmt(fait.moy_apres)}</b> (calcul officiel).
                <div className="text-xs">Lot <code>{fait.batch_id.slice(0, 8)}</code> — annulable depuis l'onglet Historique.</div>
              </div>
            </div>
          )}

          {dossier && !fait && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {dossier.proposition_auto ? (
                  <span className="text-gray-600 dark:text-gray-300">
                    Proposition intelligente : moyenne <b>{fmt(dossier.proposition_auto.moy_apres)}</b>
                    {dossier.proposition_auto.utilise_so && <span className="ml-1 text-amber-700 dark:text-amber-400">(touche la session ordinaire)</span>}
                  </span>
                ) : (
                  <span className="text-gray-600 dark:text-gray-300">
                    Pas de proposition intelligente{dossier.diagnostic_auto ? ` : ${dossier.diagnostic_auto.motif}` : ''}.
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  {dossier.proposition_auto && (
                    <button onClick={() => remplir(dossier, dossier.proposition_auto!.notes)}
                      className="px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                      Reprendre la proposition
                    </button>
                  )}
                  <button onClick={() => remplir(dossier, [])}
                    className="px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                    Tout effacer
                  </button>
                </div>
              </div>

              <table className="w-full text-xs border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 text-[10px] uppercase text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Matière</th>
                    <th className="px-2 py-1.5 text-center" title="Devoir 1 — modifiable en cas de force majeure">D1</th>
                    <th className="px-2 py-1.5 text-center" title="Devoir 2 — modifiable en cas de force majeure">D2</th>
                    <th className="px-2 py-1.5 text-right">Moy. dev.</th>
                    <th className="px-2 py-1.5 text-right">SO</th>
                    <th className="px-2 py-1.5 text-right">SR</th>
                    <th className="px-2 py-1.5 text-right">Moy. ECUE</th>
                    <th className="px-2 py-1.5 text-center">Examen à relever</th>
                    <th className="px-2 py-1.5 text-right">Maximum</th>
                  </tr>
                </thead>
                <tbody>
                  {dossier.ues.map((u) => {
                    const apres = ueApres(u.ue_id);
                    return (
                      <React.Fragment key={u.ue_id}>
                        <tr className="bg-gray-100/70 dark:bg-gray-800/70 border-t border-gray-200 dark:border-gray-700">
                          <td colSpan={9} className="px-2 py-1.5 font-semibold text-gray-800 dark:text-gray-200">
                            {u.code_ue} — {u.nom_ue}
                            <span className="ml-2 font-normal text-gray-500">
                              moyenne {fmt(u.moyenne_ue)}
                              {simulationAJour && apres && <> → <b className={apres.eliminatoire ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}>{fmt(apres.moyenne_ue)}</b></>}
                            </span>
                            {u.eliminatoire && <span className="ml-2 px-1.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px]">éliminatoire</span>}
                            {u.validee && <span className="ml-2 px-1.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-[10px]">validée</span>}
                          </td>
                        </tr>
                        {u.ecues.map((e) => {
                          const relevable = !!e.source_relevable;
                          return (
                            <tr key={e.matiere_id} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                                {e.matiere_nom}
                                {e.verrouille && <span className="ml-1 text-[10px] text-gray-400">(verrouillée)</span>}
                              </td>
                              {(['D1', 'D2'] as const).map((src) => {
                                const val = src === 'D1' ? e.d1 : e.d2;
                                const abs = src === 'D1' ? e.absent1 : e.absent2;
                                const maxi = src === 'D1' ? e.d1_max : e.d2_max;
                                return (
                                  <td key={src} className="px-1 py-1 text-center">
                                    {e.devoir_editable ? (
                                      <div className="inline-flex flex-col items-center">
                                        <span className="text-[10px] text-gray-500 tabular-nums">{abs ? 'abs' : fmt(val)}</span>
                                        <input type="number" step="0.25" min={0} max={maxi ?? 20}
                                          value={saisies[`${e.matiere_id}:${src}`] ?? ''}
                                          placeholder="—"
                                          title={`Nouvelle note ${src} (maximum ${fmt(maxi)})`}
                                          onChange={(ev) => setSaisies((s) => ({ ...s, [`${e.matiere_id}:${src}`]: ev.target.value }))}
                                          className="w-16 px-1 py-0.5 text-right border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800" />
                                      </div>
                                    ) : (
                                      <span className="tabular-nums text-gray-500" title={e.devoir_verrouille ? 'Devoirs verrouillés' : 'Aucune note de devoir'}>
                                        {abs ? 'abs' : fmt(val)}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(e.moy_devoirs)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(e.so)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(e.sr)}</td>
                              <td className={`px-2 py-1 text-right tabular-nums ${e.moyenne_ecue !== null && e.moyenne_ecue < 10 ? 'text-red-600 font-semibold' : ''}`}>
                                {fmt(e.moyenne_ecue)}
                              </td>
                              <td className="px-2 py-1 text-center">
                                {relevable ? (
                                  <div className="inline-flex items-center gap-1">
                                    <span className={`px-1 rounded text-[9px] font-bold ${e.source_relevable === 'SO'
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'}`}>
                                      {e.source_relevable}
                                    </span>
                                    <input type="number" step="0.25" min={0} max={e.note_max ?? 20}
                                      value={saisies[`${e.matiere_id}:${e.source_relevable}`] ?? ''}
                                      placeholder="—"
                                      title={`Nouvelle note ${e.source_relevable} (actuelle ${fmt(e.note_actuelle)}, maximum ${fmt(e.note_max)})`}
                                      onChange={(ev) => setSaisies((s) => ({ ...s, [`${e.matiere_id}:${e.source_relevable}`]: ev.target.value }))}
                                      className="w-20 px-1.5 py-0.5 text-right border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800" />
                                  </div>
                                ) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="px-2 py-1 text-right tabular-nums text-gray-500">{relevable ? fmt(e.note_max) : ''}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-500">
                SR = rattrapage, relevé en priorité. Une SR inférieure à la SO peut être portée au-dessus d'elle :
                c'est la meilleure des deux notes qui compte. SO = session ordinaire, proposée seulement pour une
                matière sans rattrapage composé{autoriserSoZero ? '' : ', et seulement si elle est différente de 0 (un 0 vaut non-participation)'}. Une note égale ou inférieure à la note retenue est ignorée.
                {' '}<b className="text-amber-700 dark:text-amber-400">D1 / D2 : en cas de force majeure seulement</b> —
                les devoirs sont visibles des étudiants et des formateurs ; un devoir absent relevé n'est plus absent
                (l'annulation du rachat rétablit l'absence). Les champs vides ne changent rien.
              </p>

              {problemes.length > 0 && (
                <div className="p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  {problemes.join(' · ')}
                </div>
              )}

              {resultat && (
                <div className={`p-3 rounded border text-sm ${!simulationAJour
                  ? 'border-gray-300 bg-gray-50 text-gray-500 dark:bg-gray-800 dark:border-gray-600'
                  : resultat.valide
                    ? 'border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
                    : 'border-red-300 bg-red-50 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'}`}>
                  {!simulationAJour && <div className="text-xs mb-1">Notes modifiées depuis la simulation : relancez-la.</div>}
                  Calcul officiel : moyenne {fmt(resultat.moy_avant)} → <b>{fmt(resultat.moy_apres)}</b>,
                  {' '}crédits {resultat.credits_obtenus ?? '—'}/{resultat.credits_requis ?? '—'} —
                  {' '}<b>{resultat.valide ? 'semestre validé' : 'semestre non validé'}</b>
                  {' '}({resultat.nb_notes} note{resultat.nb_notes > 1 ? 's' : ''} relevée{resultat.nb_notes > 1 ? 's' : ''}
                  {resultat.devoirs_conjoints > 0 ? `, ${resultat.devoirs_conjoints} devoir(s) aligné(s)` : ''}).
                </div>
              )}
            </>
          )}
        </div>

        {dossier && !fait && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 space-y-2">
            <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Motif ou décision du jury (facultatif, enregistré avec le rachat)"
              className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={confirme} disabled={!simulationAJour || !resultat?.valide}
                  onChange={(e) => setConfirme(e.target.checked)} />
                Je confirme le rachat de ces {notes.length} note(s)
              </label>
              <div className="flex gap-2 justify-end">
                <button onClick={lancerSimulation} disabled={simulation || notes.length === 0 || problemes.length > 0}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1">
                  {simulation && <Loader size={12} className="animate-spin" />} Simuler
                </button>
                <button onClick={lancerApplication}
                  disabled={!confirme || !simulationAJour || !resultat?.valide || application}
                  className="px-3 py-1.5 text-xs rounded bg-purple-600 hover:bg-purple-700 text-white font-medium disabled:bg-gray-300 flex items-center gap-1">
                  {application && <Loader size={12} className="animate-spin" />} Appliquer le rachat manuel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RachatManuelModal;
